import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  InputAdornment,
  TextField,
  Chip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandLessIcon,
  Download as DownloadIcon,
  Close as ClearIcon,
} from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useAllProducts } from '../../../services/products/products.service';
import {
  getDealLoversProducts,
  groupDealLoversByCategory,
  getPriceStatus,
  toDealLoversCsv,
  DEAL_LOVERS_CSV_HEADERS,
  DealLoverCategoryGroup,
} from '../dealLovers/dealLovers.utils';

interface StepDealLoversProps {
  /** Map of category name -> whole-cents string entered by the user. */
  dealLoversPrices: Record<string, string>;
  /** Update the price (whole-cents string) for a single category. */
  onPriceChange: (category: string, value: string) => void;
}

const CategoryRow: React.FC<{
  group: DealLoverCategoryGroup;
  value: string;
  onPriceChange: (category: string, value: string) => void;
}> = ({ group, value, onPriceChange }) => {
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const variantCount = group.products.reduce((sum, p) => sum + p.targetVariants.length, 0);

  const status = getPriceStatus(value);
  const showError = status === 'invalid' && touched;

  return (
    <>
      <TableRow data-testid={`dealLoverCategoryRow-${group.category}`} hover>
        <TableCell padding='checkbox'>
          <IconButton
            size='small'
            aria-label='expand category'
            onClick={() => setOpen((prev) => !prev)}
            data-testid={`dealLoverExpand-${group.category}`}
          >
            {open ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='subtitle2'>{group.category}</Typography>
            <Chip
              label={`${group.products.length} product${group.products.length === 1 ? '' : 's'} · ${variantCount} variant${variantCount === 1 ? '' : 's'}`}
              size='small'
              variant='outlined'
            />
            {status === 'valid' && (
              <Chip label='Set' color='success' size='small' data-testid={`dealLoverStatus-${group.category}`} />
            )}
          </Box>
        </TableCell>
        <TableCell align='right' sx={{ width: 200 }}>
          <TextField
            size='small'
            type='number'
            placeholder='Not set'
            value={value ?? ''}
            onChange={(e) => onPriceChange(group.category, e.target.value)}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? 'Enter a positive whole cents amount (e.g. 700)' : undefined}
            inputProps={{ min: 1, step: 1, 'data-testid': `dealLoverPrice-${group.category}` }}
            InputProps={{
              startAdornment: <InputAdornment position='start'>¢</InputAdornment>,
              endAdornment:
                value !== '' ? (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      aria-label='clear price'
                      edge='end'
                      onClick={() => {
                        onPriceChange(group.category, '');
                        setTouched(false);
                      }}
                      data-testid={`dealLoverClear-${group.category}`}
                    >
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={3}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell>Variant Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.products.flatMap((product) =>
                    product.targetVariants.map((variant, index) => (
                      <TableRow key={variant.variantId}>
                        <TableCell>
                          {index === 0 ? product.name || product.internalName : ''}
                        </TableCell>
                        <TableCell>{variant.name || '—'}</TableCell>
                        <TableCell>{variant.variantCode}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const StepDealLovers: React.FC<StepDealLoversProps> = ({ dealLoversPrices, onPriceChange }) => {
  const { data: products, isLoading, error } = useAllProducts();

  const dealLoversProducts = useMemo(
    () => getDealLoversProducts(products || []),
    [products]
  );

  const categoryGroups = useMemo(
    () => groupDealLoversByCategory(dealLoversProducts),
    [dealLoversProducts]
  );

  const csvData = useMemo(() => toDealLoversCsv(dealLoversProducts), [dealLoversProducts]);

  const setCount = useMemo(
    () =>
      categoryGroups.filter((g) => getPriceStatus(dealLoversPrices[g.category] ?? '') === 'valid')
        .length,
    [categoryGroups, dealLoversPrices]
  );

  return (
    <Card data-testid='dealLoversCard'>
      <CardHeader
        title='Deal Lovers Pricing'
        subheader={
          <Box component='span' sx={{ display: 'block', mt: 0.5 }}>
            <Typography variant='body2' color='text.secondary'>
              Set a price for any category you want to update — it applies to every Deal Lovers
              variant in that category.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Leave a category blank to skip it. Expand a row to see what’s included.
            </Typography>
          </Box>
        }
        action={
          <CSVLink
            data={csvData}
            headers={DEAL_LOVERS_CSV_HEADERS}
            filename={`deal-lovers_${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              disabled={csvData.length === 0}
              data-testid='dealLoversExportButton'
            >
              Export Current Items
            </Button>
          </CSVLink>
        }
      />
      <Divider />
      <CardContent>
        {isLoading && <LinearProgress data-testid='dealLoversLoading' />}

        {error && (
          <Alert severity='error'>
            Failed to load products. Please refresh and try again.
          </Alert>
        )}

        {!isLoading && !error && categoryGroups.length === 0 && (
          <Typography variant='body2' color='text.secondary' textAlign='center' py={3}>
            No Deal Lovers items found.
          </Typography>
        )}

        {!isLoading && !error && categoryGroups.length > 0 && (
          <TableContainer component={Paper} variant='outlined'>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant='body2' color='text.secondary' data-testid='dealLoversSetCount'>
                {setCount} of {categoryGroups.length} categories set
              </Typography>
            </Box>
            <Divider />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox' />
                  <TableCell>Category</TableCell>
                  <TableCell align='right'>Price (¢)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryGroups.map((group) => (
                  <CategoryRow
                    key={group.category}
                    group={group}
                    value={dealLoversPrices[group.category] ?? ''}
                    onPriceChange={onPriceChange}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default StepDealLovers;
